import { useEffect, useState } from 'react';
import { whenIdle } from '@/utils/whenIdle';

/**
 * `false` on mount, then `true` once the browser has gone idle — a gate for
 * work that belongs on the page but not in front of its first paint.
 *
 * Pair it with a query's `skip`/`enabled` so the request is not merely
 * deferred but genuinely off the page-load path:
 *
 * ```ts
 * const isIdle = useIdleReady();
 * useGetTaskSnapshotQuery({}, { skip: !isIdle });
 * ```
 *
 * It latches: once idle it stays `true`, so the query behaves normally from
 * then on and refetches are unaffected.
 *
 * Prefer this over {@link useNetworkIdle}, which polls
 * `performance.getEntriesByType('resource')` on a timer and exists for a
 * different job (holding a PDF capture until the page has settled).
 *
 * @param timeout ceiling in ms, not a delay — a page that never goes idle
 * still runs the callback once it elapses. See {@link whenIdle}.
 */
// Session-wide latch: the "defer off first paint" concern is one-time, so once
// the app has gone idle any later mount (e.g. a nav row the notch re-mounts on
// selection) starts ready — no second deferral, so a count badge driven by this
// gate shows its cached value immediately instead of blinking back from empty.
let hasBeenIdle = false;

export const useIdleReady = ({
  timeout,
}: { timeout?: number } = {}): boolean => {
  const [isIdle, setIsIdle] = useState(hasBeenIdle);

  useEffect(() => {
    if (hasBeenIdle) return undefined;
    // `whenIdle` returns its own canceller, which is exactly the cleanup an
    // unmount needs.
    return whenIdle(
      () => {
        hasBeenIdle = true;
        setIsIdle(true);
      },
      timeout ? { timeout } : undefined
    );
  }, [timeout]);

  return isIdle;
};

// Resets the session-wide latch; tests only, so each case starts "not yet idle".
export const resetIdleReadyLatchForTests = (): void => {
  hasBeenIdle = false;
};
